import React from 'react';
import {
  Alert, Box, Button, Chip, Grid, MenuItem, Paper, Stack, TextField, Typography,
} from '@mui/material';
import AutoCodeCombo from '../combos/AutoCodeCombo';
import { GridCellParams, GridColDef, GridValidRowModel } from '@mui/x-data-grid';
import { FDataGrid, FGridMoneyCell } from '../grid';
import { HistoricalEntryDraft } from './bieHistorical.models';

type Props = {
  rows: HistoricalEntryDraft[];
  loading?: boolean;
  metricCode: string;
  fiscalYear: string;
  allowedFiscalYears?: string[];
  canEdit?: boolean;
  onMetricChange: (code: string, option?: unknown) => void;
  onFiscalYearChange: (year: string) => void;
  onGenerate: () => Promise<void>;
  onRowsChange: (rows: HistoricalEntryDraft[]) => void;
  onSave: () => Promise<void>;
};

const statusColor = (code?: string):
  'default' | 'success' | 'warning' | 'info' | 'secondary' | 'error' => {
  switch ((code || '').toUpperCase()) {
    case 'EXISTING_ACTUAL': return 'success';
    case 'FORECAST_CANDIDATE': return 'info';
    case 'ESTIMATE_CANDIDATE': return 'secondary';
    case 'MISSING':
    case 'MANUAL_OR_IMPORT': return 'warning';
    case 'UNSUPPORTED_SCOPE': return 'error';
    default: return 'default';
  }
};

class HistoricalManualEntryGrid extends React.Component<Props> {
  private validateRow = (row: HistoricalEntryDraft): string => {
    if (row.matrixexisting) return '';
    if (!row.metriccode) return 'Historical variable is required.';
    if (!row.periodcode) return 'Period is required.';
    if ((row.raw as any)?.prq === 1 && !row.productcode) return 'Product is required.';
    if ((row.raw as any)?.lrq === 1 && !row.locationcode) return 'Location is required.';

    const numericFields: Array<[string, unknown]> = [
      ['Quantity', row.quantity],
      ['Unit Price', row.unitprice],
      ['Amount', row.amount],
    ];

    for (const [label, value] of numericFields) {
      if (!Number.isFinite(Number(value))) return `${label} must be numeric.`;
    }

    if (Number(row.quantity) < 0 || Number(row.unitprice) < 0 || Number(row.amount) < 0) {
      return 'Negative values are not permitted for this entry.';
    }
    return '';
  };

  private isEditable = (row: HistoricalEntryDraft): boolean =>
    this.props.canEdit !== false && row.editable !== false && !row.matrixexisting;

  private isQuantityFieldApplicable = (
    row: HistoricalEntryDraft,
  ): boolean => {
    return ['QUANTITY', 'VOLUME', 'COUNT'].includes(
      String((row.raw as any)?.vtc || '').toUpperCase(),
    );
  };

  private isUnitPriceFieldApplicable = (
    row: HistoricalEntryDraft,
  ): boolean => {
    return ['PRICE', 'RATE'].includes(
      String((row.raw as any)?.vtc || '').toUpperCase(),
    );
  };

  private isAmountFieldApplicable = (
    row: HistoricalEntryDraft,
  ): boolean => {
    return ['AMOUNT', 'VALUE', 'CURRENCY'].includes(
      String((row.raw as any)?.vtc || '').toUpperCase(),
    );
  };

  private isCellEditable = (
    params: GridCellParams<HistoricalEntryDraft>,
  ): boolean => {
    const row = params.row;

    if (this.props.canEdit === false) return false;
    if (row.matrixexisting) return false;
    if (row.editable === false) return false;

    switch (params.field) {
      case 'quantity':
        return this.isQuantityFieldApplicable(row);

      case 'unitprice':
        return this.isUnitPriceFieldApplicable(row);

      case 'amount':
        return this.isAmountFieldApplicable(row);

      case 'remarks':
        return true;

      default:
        return false;
    }
  };

  private columns = (): GridColDef<HistoricalEntryDraft>[] => [
    {
      field: 'metriccode',
      headerName: 'Variable',
      width: 150,
    },
    {
      field: 'productcode',
      headerName: 'Product',
      width: 125,
      valueGetter: (_value, row) => row.productcode || '—',
    },
    {
      field: 'productname',
      headerName: 'Product Name',
      flex: 1,
      minWidth: 180,
    },
    {
      field: 'locationcode',
      headerName: 'Location',
      width: 120,
      valueGetter: (_value, row) => row.locationcode || '—',
    },
    {
      field: 'periodcode',
      headerName: 'Period',
      width: 120,
    },
    {
      field: 'matrixstatusname',
      headerName: 'Matrix Status',
      width: 175,
      renderCell: (params) => (
        <Chip
          size="small"
          color={statusColor(params.row.matrixstatuscode)}
          label={params.row.matrixstatusname || 'Needs Resolution'}
        />
      ),
    },
    {
      field: 'resolutionname',
      headerName: 'Recommended Resolution',
      width: 220,
      renderCell: (params) => (
        <Typography variant="body2">
          {params.row.resolutionname || 'Review'}
        </Typography>
      ),
    },
    {
      field: 'quantity',
      headerName: 'Quantity',
      width: 125,
      type: 'number',
      editable: true,
    },
    {
      field: 'unitcode',
      headerName: 'Unit',
      width: 85,
    },
    {
      field: 'unitprice',
      headerName: 'Unit Price',
      width: 130,
      type: 'number',
      editable: true,
      renderCell: FGridMoneyCell,
    },
    {
      field: 'amount',
      headerName: 'Amount',
      width: 150,
      type: 'number',
      editable: true,
      renderCell: FGridMoneyCell,
    },
    {
      field: 'remarks',
      headerName: 'Remarks',
      flex: 1,
      minWidth: 190,
      editable: true,
    },
    {
      field: '__error',
      headerName: 'Validation',
      width: 210,
    },
  ];

  private columns1 = (): GridColDef[] => [
    { field: 'metriccode', headerName: 'Variable', width: 150 },
    {
      field: 'productcode',
      headerName: 'Product',
      width: 125,
      valueGetter: (_value, row) => row.productcode || '—',
    },
    { field: 'productname', headerName: 'Product Name', flex: 1, minWidth: 180 },
    {
      field: 'locationcode',
      headerName: 'Location',
      width: 120,
      valueGetter: (_value, row) => row.locationcode || '—',
    },
    { field: 'periodcode', headerName: 'Period', width: 120 },
    {
      field: 'matrixstatusname',
      headerName: 'Matrix Status',
      width: 175,
      renderCell: (params) => (
        <Chip
          size="small"
          color={statusColor(params.row.matrixstatuscode)}
          label={params.row.matrixstatusname || 'Needs Resolution'}
        />
      ),
    },
    {
      field: 'resolutionname',
      headerName: 'Recommended Resolution',
      width: 220,
      renderCell: (params) => (
        <Typography variant="body2">
          {params.row.resolutionname || 'Review'}
        </Typography>
      ),
    },
    {
      field: 'quantity',
      headerName: 'Quantity',
      width: 125,
      type: 'number',
      //editable: this.isEditable(params.row),
    },
    { field: 'unitcode', headerName: 'Unit', width: 85 },
    {
      field: 'unitprice',
      headerName: 'Unit Price',
      width: 130,
      //editable: (params) => this.isEditable(params.row),
      renderCell: FGridMoneyCell,
    },
    {
      field: 'amount',
      headerName: 'Amount',
      width: 150,
      //editable: (params) => this.isEditable(params.row),
      renderCell: FGridMoneyCell,
    },
    {
      field: 'remarks',
      headerName: 'Remarks',
      flex: 1,
      minWidth: 190,
      //editable: (params) => this.isEditable(params.row),
    },
    { field: '__error', headerName: 'Validation', width: 210 },
  ];

  private processRowUpdate = (newRow: GridValidRowModel, oldRow: GridValidRowModel) => {
    if (oldRow.matrixexisting || oldRow.editable === false) return oldRow;

    const hasExplicitAmount =
      newRow.amount !== null &&
      newRow.amount !== undefined &&
      newRow.amount !== '';

    const quantity = Number(newRow.quantity || 0);
    const unitprice = Number(newRow.unitprice || 0);
    const amount = hasExplicitAmount ? Number(newRow.amount) : quantity * unitprice;

    const updated: HistoricalEntryDraft = {
      ...(newRow as HistoricalEntryDraft),
      quantity,
      unitprice,
      amount,
      __dirty: true,
      __new: true,
    };
    updated.__error = this.validateRow(updated);

    this.props.onRowsChange(
      this.props.rows.map((row) =>
        String(row.rid) === String(oldRow.rid) ? updated : row,
      ),
    );
    return updated;
  };

  render() {
    const actionableRows = this.props.rows.filter((row) => !row.matrixexisting);
    const dirtyCount = actionableRows.filter((row) => row.__dirty).length;
    const invalidCount = actionableRows.filter((row) => Boolean(row.__error || this.validateRow(row))).length;
    const existingCount = this.props.rows.filter((row) => row.matrixexisting).length;
    const estimateCount = this.props.rows.filter((row) => row.matrixstatuscode === 'ESTIMATE_CANDIDATE').length;
    const forecastCount = this.props.rows.filter((row) => row.matrixstatuscode === 'FORECAST_CANDIDATE').length;
    const years = this.props.allowedFiscalYears || [];

    return (
      <Stack spacing={2}>
        <Paper elevation={1} sx={{ p: 2 }}>
          <Stack spacing={2}>
            <div>
              <Typography variant="h6" sx={{ fontWeight: 800 }}>BPMMS Matrix</Typography>
              <Typography variant="body2" color="text.secondary">
                Select one historical variable and a coverage year. The Matrix derives its periods and required subjects,
                compares them with the institutional repository, and presents only the decisions that still need you.
              </Typography>
            </div>

            <Grid container spacing={2}>
              <Grid size={{ xs: 12, md: 5 }}>
                <AutoCodeCombo
                  service="biehistoricalmetric"
                  label="Historical Variable"
                  value={this.props.metricCode || null}
                  onChange={(value, option) => this.props.onMetricChange(String(value || ''), option)}
                  params={{ hst: 1 }}
                  minimumSearchLength={0}
                  disabled={this.props.canEdit === false}
                />
              </Grid>

              <Grid size={{ xs: 12, md: 3 }}>
                <TextField
                  select={years.length > 0}
                  size="small"
                  label="Coverage Year"
                  value={this.props.fiscalYear}
                  onChange={(event) => this.props.onFiscalYearChange(event.target.value)}
                  fullWidth
                  disabled={this.props.canEdit === false}
                >
                  {years.map((year) => <MenuItem key={year} value={year}>{year}</MenuItem>)}
                </TextField>
              </Grid>

              <Grid size={{ xs: 12, md: 4 }}>
                <Button
                  variant="contained"
                  onClick={() => void this.props.onGenerate()}
                  disabled={
                    this.props.loading ||
                    this.props.canEdit === false ||
                    !this.props.metricCode ||
                    !this.props.fiscalYear ||
                    dirtyCount > 0
                  }
                  fullWidth
                  sx={{ fontWeight: 900, letterSpacing: 1.2 }}
                >
                  ENTER THE MATRIX
                </Button>
              </Grid>
            </Grid>
          </Stack>
        </Paper>

        {!!this.props.rows.length && (
          <Grid container spacing={1.5}>
            {[
              ['Expected Rows', this.props.rows.length],
              ['Existing Actuals', existingCount],
              ['Unresolved Gaps', actionableRows.length],
              ['Estimate Candidates', estimateCount],
              ['Forecast Candidates', forecastCount],
            ].map(([label, value]) => (
              <Grid key={String(label)} size={{ xs: 6, sm: 4, md: 2.4 }}>
                <Paper variant="outlined" sx={{ p: 1.5 }}>
                  <Typography variant="caption" color="text.secondary">{label}</Typography>
                  <Typography variant="h6" sx={{ fontWeight: 900 }}>{value}</Typography>
                </Paper>
              </Grid>
            ))}
          </Grid>
        )}

        {dirtyCount > 0 && (
          <Alert severity={invalidCount > 0 ? 'warning' : 'info'}>
            {dirtyCount} unsaved row(s). {invalidCount > 0
              ? `${invalidCount} row(s) require correction before saving.`
              : 'Save or discard them before entering another Matrix.'}
          </Alert>
        )}

        {!this.props.rows.length && (
          <Alert severity="info">
            Enter the Matrix to construct the expected baseline, reveal existing facts and classify unresolved gaps.
          </Alert>
        )}

        <FDataGrid
          title="Expected Historical Baseline"
          subtitle="Green rows already exist. Only unresolved rows are editable and only changed rows are submitted."
          rows={this.props.rows}
          columns={this.columns()}
          isCellEditable={this.isCellEditable}
          loading={this.props.loading}
          height={560}
          getRowId={(row: HistoricalEntryDraft) => row.rid}
          processRowUpdate={this.processRowUpdate}
          //onProcessRowUpdateError={this.handleProcessRowUpdateError}
          onProcessRowUpdateError={(error: unknown) => console.error(error)}
          getRowClassName={(params) =>
            params.row.matrixexisting
              ? 'matrix-row-existing'
              : params.row.__error
                ? 'matrix-row-error'
                : params.row.__dirty
                  ? 'matrix-row-dirty'
                  : 'matrix-row-gap'
          }
          showQuickFilter
          saveAll={invalidCount > 0 || dirtyCount === 0 || this.props.canEdit === false
            ? undefined
            : this.props.onSave}
          pageSizeOptions={[25, 50, 100]}
          sx={{
            '& .matrix-row-existing': { bgcolor: 'success.lighter' },
            '& .matrix-row-gap': { bgcolor: 'warning.lighter' },
            '& .matrix-row-error': { bgcolor: 'error.lighter' },
            '& .matrix-row-dirty': { bgcolor: 'action.hover' },
          }}
        />
      </Stack>
    );
  }
}

export default HistoricalManualEntryGrid;
